import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Card,
    CardBody,
    Flex,
    Icon,
    Input,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Badge,
    Spinner,
    InputGroup,
    InputLeftElement,
    Select,
} from '@chakra-ui/react';
import { FaUserPlus, FaUsers, FaEnvelope, FaLock, FaIdCard, FaCog } from 'react-icons/fa';
import ConfirmDialog from '../../compnents/ConfirmDialog';
import EmptyStateCard from '../../compnents/EmptyStateCard';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const employeeRoleOptions = [
    { value: 'rm', label: 'Relationship Manager', businessOnly: false },
    { value: 'compliance', label: 'Compliance Officer', businessOnly: true },
    { value: 'head', label: 'Head of Advisory', businessOnly: true },
    { value: 'portfolio_adviser', label: 'Portfolio Adviser', businessOnly: true },
    { value: 'view_only', label: 'View Only', businessOnly: true },
];

const getRoleLabel = (role) => {
    const match = employeeRoleOptions.find((option) => option.value === role);
    if (match) {
        return match.label;
    }

    if (role === 'admin') {
        return 'Admin';
    }

    if (role === 'view') {
        return 'View Only';
    }

    return role;
};

function SettingsEmployeeManagementPaid() {
    const { token, user } = useContext(AuthContext);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        password: '',
        role: 'rm',
    });
    const [formErrors, setFormErrors] = useState({});
    const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();
    const [resetEmployee, setResetEmployee] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [employeePendingDelete, setEmployeePendingDelete] = useState(null);
    const [deletingEmployeeEmail, setDeletingEmployeeEmail] = useState(null);
    const canCreateBusinessRoles = user?.subscription === 'business';
    const availableEmployeeRoleOptions = employeeRoleOptions.filter(
        (option) => !option.businessOnly || canCreateBusinessRoles
    );

    // People with access against the plan (owner included), so a full plan is
    // explained before the form is filled instead of after it is submitted.
    const [seatUsage, setSeatUsage] = useState(null);
    const fetchSeatUsage = async () => {
        try {
            const { data } = await axios.get(`${API_BASE_URL}/subscription/usage`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSeatUsage(data?.seats || null);
        } catch (_) {
            setSeatUsage(null); // the server still enforces the limit on creation
        }
    };
    const seatsFull = Number.isFinite(seatUsage?.limit) && seatUsage.used >= seatUsage.limit;

    useEffect(() => {
        fetchEmployees();
        fetchSeatUsage();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const selectedRole = employeeRoleOptions.find((option) => option.value === formData.role);
        if (selectedRole?.businessOnly && !canCreateBusinessRoles) {
            setFormData((prev) => ({ ...prev, role: 'rm' }));
        }
    }, [canCreateBusinessRoles, formData.role]);

    const fetchEmployees = async () => {
        setLoadingEmployees(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/roles/list-all-employees`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmployees(response.data.employees || []);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to fetch employees',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoadingEmployees(false);
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format';
        }

        if (!formData.password) {
            errors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }

        if (!formData.role) {
            errors.role = 'Role is required';
        }

        const selectedRole = employeeRoleOptions.find((option) => option.value === formData.role);
        if (selectedRole?.businessOnly && !canCreateBusinessRoles) {
            errors.role = 'Compliance, Head of Advisory, Portfolio Adviser, and View Only are Business plan roles.';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCreateEmployee = async () => {
        if (!validateForm()) {
            return;
        }

        const selectedRole = employeeRoleOptions.find((option) => option.value === formData.role);
        if (selectedRole?.businessOnly && !canCreateBusinessRoles) {
            toast({
                title: 'Business plan required',
                description: 'Team can create Relationship Manager users only. Business adds Compliance, Head of Advisory, Portfolio Adviser and View Only roles.',
                status: 'warning',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/roles/create-new-employee`,
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast({
                title: 'Success!',
                description: response.data.message || `${getRoleLabel(formData.role)} added successfully`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Reset form and close modal
            setFormData({ email: '', name: '', password: '', role: 'rm' });
            setFormErrors({});
            onClose();

            // Refresh employee list
            fetchEmployees();
            fetchSeatUsage();
        } catch (error) {
            const code = error.response?.data?.code;
            toast({
                title: code === 'SEAT_LIMIT_REACHED' ? 'All included people are in use' : 'Error',
                description: code === 'SEAT_LIMIT_REACHED'
                    ? `${error.response.data.message} Extra people are added from the Subscription page.`
                    : error.response?.data?.message || 'Failed to create employee',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEmployee = async () => {
        if (!employeePendingDelete?.email) {
            return;
        }

        const { email } = employeePendingDelete;
        setDeletingEmployeeEmail(email);
        try {
            const response = await axios.delete(`${API_BASE_URL}/roles/delete-employee`, {
                headers: { Authorization: `Bearer ${token}` },
                data: { email }, // 👈 DELETE requires 'data' field for body
            });

            toast({
                title: 'Success',
                description: response.data.message || 'Employee deleted successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Refresh employee list
            fetchEmployees();
            fetchSeatUsage();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete employee',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDeletingEmployeeEmail(null);
            setEmployeePendingDelete(null);
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) {
            setPasswordError('Password is required');
            return;
        }
        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            return;
        }

        setResetLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL}/auth/reset-rm-password`,
                {
                    email: resetEmployee.email,
                    newPassword: newPassword,
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast({
                title: 'Success',
                description: response.data.message || 'Password reset successfully',
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });

            // Reset and close
            setNewPassword('');
            setPasswordError('');
            setResetEmployee(null);
            onResetClose();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to reset password',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setResetLoading(false);
        }
    };

    const openResetModal = (employee) => {
        setResetEmployee(employee);
        setNewPassword('');
        setPasswordError('');
        onResetOpen();
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            admin: 'blue',
            rm: 'teal',
            compliance: 'blue',
            head: 'orange',
            portfolio_adviser: 'teal',
            view: 'green',
            view_only: 'green',
        };
        return colors[role] || 'gray';
    };

    return (
        <Container maxW="container.xl" mt={4}>
            <VStack spacing={10} align="stretch">
                {/* 
                <Box textAlign="center">
                    <Text color="gray.600" fontSize="md">
                        Create and manage roles for your organization
                    </Text>
                </Box>
                */}

                {/* Action Button */}
                <Flex
                    justify="space-between"
                    align="center"
                    bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                    color="white"
                    p={6}
                    borderRadius="xl"
                    boxShadow="xl"
                    >
                        <HStack spacing={3}>
                            <Icon as={FaUsers} boxSize={7} />
                        <Box>
                            <Text fontSize="sm" opacity={0.9}>
                                Total Employees
                            </Text>
                            <Heading size="lg">{employees.length}</Heading>
                            <Text fontSize="xs" opacity={0.85}>
                                {canCreateBusinessRoles
                                    ? 'Business tenant: all role families are available.'
                                    : 'Team: you can add Relationship Manager users.'}
                            </Text>
                            {seatUsage && Number.isFinite(seatUsage.limit) && (
                                <Text fontSize="xs" opacity={0.85} data-testid="seat-usage">
                                    People with access: {seatUsage.used} of {seatUsage.limit}, you included.
                                </Text>
                            )}
                        </Box>
                    </HStack>

                    <Button
                        leftIcon={<FaUserPlus />}
                        onClick={onOpen}
                        variant="solid"
                        colorScheme="blackAlpha"
                        size="lg"
                        _hover={{ bg: 'blackAlpha.400' }}
                        color="white"
                        isDisabled={seatsFull}
                    >
                        Add Employee Role
                    </Button>
                </Flex>

                {seatsFull && (
                    <Alert status="info" borderRadius="lg" mt={4} data-testid="seat-limit-notice">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                            All {seatUsage.limit} people included in your plan already have access. To add someone,
                            add a person from the Subscription page or remove an access you no longer need.
                        </AlertDescription>
                    </Alert>
                )}

                {/* Employee List */}
                <Card
                    borderRadius="2xl"
                    boxShadow="xl"
                    overflow="hidden"
                >
                    <CardBody p={0}>
                        <Box
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            color="white"
                            p={6}
                        >
                            <Heading size="md">
                                <HStack spacing={2}>
                                    <Icon as={FaUsers} />
                                    <Text>Role Directory</Text>
                                </HStack>
                            </Heading>
                        </Box>

                        <Box p={6}>
                            {loadingEmployees ? (
                                <Flex justify="center" align="center" py={10}>
                                    <Spinner
                                        thickness="4px"
                                        speed="0.65s"
                                        emptyColor="gray.200"
                                        color="brand.tealDark"
                                        size="xl"
                                    />
                                </Flex>
                            ) : employees.length === 0 ? (
                                <EmptyStateCard
                                    icon={FaUsers}
                                    title="Build your team directory"
                                    description="Invite employee roles from this workspace so each team can log in with role-specific access."
                                    hint="Start with RMs for onboarding, then add compliance, advisory leadership, or view-only accounts as the rollout expands."
                                    borderColor="brand.divider"
                                    titleColor="gray.700"
                                    descriptionColor="gray.600"
                                    hintColor="gray.500"
                                    iconColor="brand.tealDark"
                                    maxW="full"
                                >
                                    <Button leftIcon={<FaUserPlus />} colorScheme="teal" onClick={onOpen} isDisabled={seatsFull}>
                                        Add Employee Role
                                    </Button>
                                </EmptyStateCard>
                            ) : (
                                <TableContainer>
                                    <Table variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color="gray.600"
                                                    fontWeight="bold"
                                                >
                                                    Name
                                                </Th>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color="gray.600"
                                                    fontWeight="bold"
                                                >
                                                    Email
                                                </Th>
                                                <Th
                                                    fontSize="sm"
                                                    textTransform="uppercase"
                                                    color="gray.600"
                                                    fontWeight="bold"
                                                >
                                                    Role
                                                </Th>
                                                <Th>Action</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {employees.map((employee, index) => (
                                                <Tr key={index} _hover={{ bg: 'gray.50' }}>
                                                    <Td fontWeight="medium">{employee.name}</Td>
                                                    <Td color="gray.600">{employee.email}</Td>
                                                    <Td>
                                                        <Badge
                                                            colorScheme={getRoleBadgeColor(employee.role)}
                                                            fontSize="sm"
                                                            px={3}
                                                            py={1}
                                                            borderRadius="full"
                                                            textTransform="uppercase"
                                                        >
                                                            {getRoleLabel(employee.role)}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <HStack spacing={2}>
                                                            <Button
                                                                colorScheme="teal"
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<FaCog />}
                                                                onClick={() => openResetModal(employee)}
                                                            >
                                                                Settings
                                                            </Button>
                                                            <Button
                                                                colorScheme="red"
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => setEmployeePendingDelete(employee)}
                                                                isLoading={deletingEmployeeEmail === employee.email}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        {/* <HStack justifyContent={"center"} spacing={2} mt={4}>
                            <Text textAlign={"center"} my={5} color="gray.600">To update name/email for any employee, please contact FinanSee support</Text>
                        </HStack> */}
                    </CardBody>
                </Card>
            </VStack>

            {/* Create Employee Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(6px)" />
                    <ModalContent borderRadius="xl">
                        <ModalHeader>
                            <HStack spacing={2}>
                                <Icon as={FaUserPlus} color="brand.tealDark" />
                                <Text>Add Employee Role</Text>
                            </HStack>
                        </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <VStack spacing={5} align="stretch">
                            <Box
                                p={4}
                                bg={canCreateBusinessRoles ? 'brand.tealSoft' : 'brand.tealSoft'}
                                borderRadius="lg"
                                borderWidth="1px"
                                borderColor={canCreateBusinessRoles ? 'brand.divider' : 'brand.divider'}
                            >
                                <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                                    {canCreateBusinessRoles ? 'Business role creation' : 'Team roles'}
                                </Text>
                                <Text fontSize="sm" color="gray.600" mt={1}>
                                    {canCreateBusinessRoles
                                        ? 'You can create RM, Compliance, Head of Advisory, Portfolio Adviser, and View Only accounts.'
                                        : 'Team includes Admin and RM workflows. Compliance, Head of Advisory, Portfolio Adviser and View Only require Business.'}
                                </Text>
                            </Box>

                            <FormControl isInvalid={formErrors.name}>
                                <FormLabel fontWeight="semibold" color="gray.700">
                                    Full Name
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaIdCard} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter full name"
                                        size="lg"
                                        focusBorderColor="brand.teal"
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.name}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.email}>
                                <FormLabel fontWeight="semibold" color="gray.700">
                                    Email Address
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaEnvelope} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        size="lg"
                                        focusBorderColor="brand.teal"
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.email}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.password}>
                                <FormLabel fontWeight="semibold" color="gray.700">
                                    Password
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaLock} color="gray.400" />
                                    </InputLeftElement>
                                    <Input
                                        name="password"
                                        type="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Enter password"
                                        size="lg"
                                        focusBorderColor="brand.teal"
                                    />
                                </InputGroup>
                                <FormErrorMessage>{formErrors.password}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={formErrors.role}>
                                <FormLabel fontWeight="semibold" color="gray.700">
                                    Role
                                </FormLabel>
                                <Select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    size="lg"
                                    focusBorderColor="brand.teal"
                                >
                                    {availableEmployeeRoleOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Select>
                                <FormErrorMessage>{formErrors.role}</FormErrorMessage>
                            </FormControl>

                            <Box
                                p={4}
                                bg="brand.tealSoft"
                                borderRadius="lg"
                                borderLeft="4px solid"
                                borderColor="brand.teal"
                            >
                                <Text fontSize="sm" color="gray.600" mb={1}>
                                    Role Assignment
                                </Text>
                                <Badge
                                    colorScheme={getRoleBadgeColor(formData.role)}
                                    fontSize="md"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    textTransform="uppercase"
                                >
                                    {getRoleLabel(formData.role)}
                                </Badge>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={() => {
                                onClose();
                                setFormData({ email: '', name: '', password: '', role: 'rm' });
                                setFormErrors({});
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={handleCreateEmployee}
                            isLoading={loading}
                            loadingText="Creating..."
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            _hover={{
                                bgGradient: 'linear(to-r, brand.navy, brand.tealDark)',
                            }}
                        >
                            Create Employee
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Reset Password Modal */}
            <Modal isOpen={isResetOpen} onClose={() => {
                onResetClose();
                setNewPassword('');
                setPasswordError('');
                setResetEmployee(null);
            }} isCentered size="md">
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="xl">
                    <ModalHeader>
                        <HStack spacing={2}>
                            <Icon as={FaCog} color="brand.tealDark" />
                            <Text>Reset Password</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {resetEmployee && (
                            <VStack spacing={4} align="stretch">
                                <Box
                                    p={3}
                                    bg="gray.50"
                                    borderRadius="md"
                                    borderLeft="3px solid"
                                    borderColor="brand.teal"
                                >
                                    <Text fontSize="sm" color="gray.600" mb={1}>
                                        Employee
                                    </Text>
                                    <Text fontWeight="semibold" fontSize="md">
                                        {resetEmployee.name}
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        {resetEmployee.email}
                                    </Text>
                                </Box>

                                <FormControl isInvalid={passwordError}>
                                    <FormLabel fontWeight="semibold" color="gray.700">
                                        New Password
                                    </FormLabel>
                                    <InputGroup>
                                        <InputLeftElement pointerEvents="none">
                                            <Icon as={FaLock} color="gray.400" />
                                        </InputLeftElement>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => {
                                                setNewPassword(e.target.value);
                                                setPasswordError('');
                                            }}
                                            placeholder="Enter new password"
                                            size="lg"
                                            focusBorderColor="brand.teal"
                                        />
                                    </InputGroup>
                                    <FormErrorMessage>{passwordError}</FormErrorMessage>
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={() => {
                                onResetClose();
                                setNewPassword('');
                                setPasswordError('');
                                setResetEmployee(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={handleResetPassword}
                            isLoading={resetLoading}
                            loadingText="Resetting..."
                            bgGradient="linear(to-r, brand.navy, brand.tealDark)"
                            _hover={{
                                bgGradient: 'linear(to-r, brand.navy, brand.tealDark)',
                            }}
                        >
                            Reset Password
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ConfirmDialog
                isOpen={Boolean(employeePendingDelete)}
                onClose={() => setEmployeePendingDelete(null)}
                onConfirm={handleDeleteEmployee}
                title="Delete Employee"
                description={
                    employeePendingDelete
                        ? `Are you sure you want to delete ${employeePendingDelete.email}?`
                        : ''
                }
                confirmLabel="Delete Employee"
                isLoading={Boolean(deletingEmployeeEmail)}
            />
        </Container>
    );
}

export default SettingsEmployeeManagementPaid;
